import { useState } from 'react'
import { Box, CircularProgress, Stack, Typography, styled, Divider } from '@mui/material'
import { useQuery } from '@tanstack/react-query'

import { Accordion } from '@/components/UI/Accordion'
import { ApplicationSummary } from '@/components/ApplicationSummary'
import { Chip } from '@/components/UI/Chip'
import { IApplication } from '@/types'
import { useAppStore } from '@/store'
import SearchLogo from '@/assets/search.svg'
import { ApplicationService } from '@/api/services/ApplicationService'
import { ErrorPage } from '@/pages/Error'
import { Rules } from '../Rules'

export const LoanApplications = () => {
  const [expanded, setExpanded] = useState<string | false>(false)
  const { setSelectedApplication } = useAppStore()

  const {
    data: applications = [],
    isLoading,
    isError,
  } = useQuery<IApplication[], Error>({
    queryKey: ['loan-application'],
    queryFn: ApplicationService.fetchApplications,
  })

  const handleAccordionChange = (panel: string, application: IApplication) => {
    setExpanded(expanded === panel ? false : panel)
    setSelectedApplication(application)
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress size={25} sx={{ color: 'lightgray' }} aria-label="Loading applications" />
      </Box>
    )
  }

  if (isError) {
    return (
      <ErrorPage
        title="Unable to Fetch Loan Applications"
        message={
          "We couldn't retrieve customer data at the moment. Please check your internet connection or try again later.\nIf the issue persists, contact administrator."
        }
      />
    )
  }

  if (applications.length === 0) {
    return (
      <EmptyContainer>
        <Box component="img" src={SearchLogo} alt="Search Icon" mb={2} />
        <Title>No Applications Found</Title>
      </EmptyContainer>
    )
  }

  return (
    <>
      <Box
        sx={{
          minWidth: "100%",
          overflowX: 'auto',
        }}
      >
        <Typography variant="h6" pb={1}>
          Applications
        </Typography>
        {applications.map((item: IApplication) => (
          <Box mb={2} key={item.id} minWidth={'280px'}>
            <Accordion
              summary={
                <Stack alignItems="center" direction="row" gap={2}>
                  <Typography>{item.reference}</Typography>
                  <Chip value={item.status} />
                </Stack>
              }
              content={
                <Box sx={{ border: '1px solid #EAECF0', borderBottom: 'none' }}>
                  <Box p={2}>
                    <ApplicationSummary />
                  </Box>
                  <Divider sx={{ borderColor: '#EAECF0' }} />
                  <Rules />
                </Box>
              }
              expanded={expanded === String(item.id)}
              onChange={() => handleAccordionChange(String(item.id), item)}
            />
          </Box>
        ))}
      </Box>
    </>
  )
}

const EmptyContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  border: '1px solid #E4E7EC',
  borderRadius: '8px',
  padding: '3rem 0',
})

const Title = styled(Typography)(({ theme }) => ({
  ...theme.typography.h6,
  fontSize: '0.9rem',
  paddingBottom: theme.spacing(3),
}))